import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useT } from '@kmgestao/i18n';
import {
  Badge,
  Button,
  Input,
  PageContainer,
  PageIntroCard,
  PageSectionCard,
  Select,
  Skeleton,
} from '@kmgestao/ui';

import {
  ACTIVE_LEAD_STAGES,
  LEAD_STAGE_LABELS,
  LEAD_STAGES,
  countLeadsByStage,
  useLeads,
  type LeadStage,
} from '../../../features/leads/index.js';
import { SuperadminShell } from '../SuperadminShell.js';

import { GenerateProspectLinkDialog } from './GenerateProspectLinkDialog.js';
import { LeadsCardList } from './LeadsCardList.js';
import { LeadsKanbanBoard } from './LeadsKanbanBoard.js';
import { LeadsTable } from './LeadsTable.js';
import { NewLeadDialog } from './NewLeadDialog.js';

type LeadsView = 'list' | 'kanban';

function parseView(value: string | null): LeadsView {
  return value === 'kanban' ? 'kanban' : 'list';
}

/**
 * Tela principal de leads — canônica pro funil comercial da Karla.
 *
 * **Padrão responsivo** (docs/design-system.md §Mobile-First):
 *   - `< sm` (< 640px, alvo dos 375/390 mobile): lista de cards empilhados
 *     verticalmente, cada lead com nome + clínica + badge de estado.
 *     Tap-alvo inteiro do card navega pro detalhe.
 *   - `≥ sm`: tabela densa com colunas (nome / clínica / email / CNPJ /
 *     estado / criado).
 *
 * **Filtros**: busca por texto + select de estado + toggle arquivados.
 * Kanban visual registrado para M1-02.
 */
export function LeadsListPage() {
  const { t: tClinics } = useT('clinics');
  const [searchParams, setSearchParams] = useSearchParams();
  const view = parseView(searchParams.get('view'));
  const setView = (next: LeadsView) => {
    // Preserva o resto dos params (filtros futuros), só troca `view`.
    const sp = new URLSearchParams(searchParams);
    if (next === 'kanban') sp.set('view', 'kanban');
    else sp.delete('view');
    setSearchParams(sp, { replace: true });
  };

  const [showArchived, setShowArchived] = useState(false);
  const [stageFilter, setStageFilter] = useState<LeadStage | 'all'>('all');
  const [search, setSearch] = useState('');

  const stagesToFetch = showArchived ? ('all' as const) : ACTIVE_LEAD_STAGES;
  const { data: leads, isLoading, isError, error } = useLeads(stagesToFetch);

  const filtered = useMemo(() => {
    if (!leads) return [];
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (stageFilter !== 'all' && l.stage !== stageFilter) return false;
      if (q) {
        const hay = `${l.full_name} ${l.clinic_name_guess ?? ''} ${l.email}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [leads, stageFilter, search]);

  const counts = useMemo(() => (leads ? countLeadsByStage(leads) : null), [leads]);

  return (
    <SuperadminShell>
      <PageContainer>
        <PageIntroCard
          eyebrow="Superadmin › Funil comercial"
          title="Leads"
          description={
            'Cadastre leads, envie o questionário de onboarding, acompanhe ' +
            'respostas e gere propostas. Cada lead tem uma timeline completa ' +
            'de interações.'
          }
          actions={
            <div className="flex flex-wrap gap-2">
              <GenerateProspectLinkDialog />
              <NewLeadDialog />
            </div>
          }
        >
          {counts ? (
            <div className="flex flex-wrap gap-2 pt-3">
              {LEAD_STAGES.filter((s) => counts[s] > 0).map((s) => {
                const active = stageFilter === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStageFilter((cur) => (cur === s ? 'all' : s))}
                    className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    aria-pressed={active}
                  >
                    <Badge variant={active ? 'default' : 'outline'}>
                      {LEAD_STAGE_LABELS[s]} · {counts[s]}
                    </Badge>
                  </button>
                );
              })}
            </div>
          ) : null}
        </PageIntroCard>

        <PageSectionCard
          title="Filtros"
          description="Busca por texto + seleção de estado. Sem lead arquivado por padrão."
        >
          <div className="grid gap-3 sm:grid-cols-[1fr,auto] lg:grid-cols-[1fr,auto,auto]">
            <Input
              placeholder="Buscar por nome, clínica ou email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputMode="search"
              autoComplete="off"
              aria-label="Buscar leads"
            />
            <Select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value as LeadStage | 'all')}
              aria-label="Filtrar por estado"
              className="sm:min-w-[12rem]"
            >
              <option value="all">Todos os estados</option>
              {LEAD_STAGES.map((s) => (
                <option key={s} value={s}>
                  {LEAD_STAGE_LABELS[s]}
                </option>
              ))}
            </Select>
            <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="size-4 rounded border-input accent-primary"
              />
              Incluir arquivados
            </label>
          </div>
        </PageSectionCard>

        <PageSectionCard
          title={view === 'kanban' ? 'Kanban' : 'Lista de leads'}
          description={
            isLoading
              ? 'Carregando…'
              : `${filtered.length} lead${filtered.length === 1 ? '' : 's'} ${
                  search || stageFilter !== 'all' ? 'visível' : 'ativo'
                }${filtered.length === 1 ? '' : 's'}.`
          }
          action={
            <div
              role="group"
              aria-label={tClinics('kanban.viewToggle.ariaLabel')}
              className="inline-flex rounded-md border border-border bg-card p-0.5"
            >
              <Button
                type="button"
                size="sm"
                variant={view === 'list' ? 'default' : 'ghost'}
                onClick={() => setView('list')}
                aria-pressed={view === 'list'}
              >
                {tClinics('kanban.viewToggle.list')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={view === 'kanban' ? 'default' : 'ghost'}
                onClick={() => setView('kanban')}
                aria-pressed={view === 'kanban'}
              >
                {tClinics('kanban.viewToggle.kanban')}
              </Button>
            </div>
          }
        >
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              <p className="font-semibold">Falha ao carregar leads.</p>
              <p className="mt-1 font-mono text-xs">{error?.message}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {search || stageFilter !== 'all'
                ? 'Nenhum lead bate com os filtros atuais.'
                : 'Nenhum lead ainda. Use o botão "Novo lead" para cadastrar o primeiro.'}
            </div>
          ) : view === 'kanban' ? (
            <LeadsKanbanBoard leads={filtered} includeLost={showArchived} includeProvisioned />
          ) : (
            <>
              {/* Mobile (< sm): cards extraídos em LeadsCardList.tsx.
                  Layout: badge (eyebrow) + nome destaque + clínica/email/relativo.
                  Border lateral accent em milestones via cardClassName. */}
              <LeadsCardList leads={filtered} />

              {/* Desktop (≥ sm): tabela densa em LeadsTable.tsx. */}
              <LeadsTable leads={filtered} />
            </>
          )}
        </PageSectionCard>
      </PageContainer>
    </SuperadminShell>
  );
}
